import { useRouter } from "next/router";

import { objectTypes, actionNames } from "@newsela/snitch";
import {
  getSuccessToastProps,
  getErrorToastProps,
} from "@/assignments/utils/assignmentToastHelpers";
import { getIsSavedAssignment } from "@/assignments/utils/assignmentUrlHelpers";
import { getIsAssignmentValid } from "@/assignments/utils/assignmentValidationHelpers";
import globalToasterRef from "@/base/globalToasterRef";

import focusElementRefKeys from "@/view/constants/focusElementRefKeys";
import useFocusManager from "@/view/utils/useFocusManager";

const useSaveDraftAssignment = ({
  currentContentLevelId,
  handleSave,
  isDisabled = false,
  updatedAssignment,
}) => {
  const router = useRouter();
  const { selectedAssignmentId } = router.query;
  const isSavedAssignment = getIsSavedAssignment(selectedAssignmentId);
  const assignmentId = updatedAssignment?.content_id;
  const isPublishedAssignment = !!assignmentId && !updatedAssignment?.is_draft;
  const { focusElement } = useFocusManager();

  const getEventProperties = (actionName, description) => ({
    object_type: objectTypes.assignment,
    action_name: actionName,
    dimension_properties: {
      content_id: currentContentLevelId,
      content_ids: updatedAssignment?.assignment_members?.map(
        (member) => member.content_id,
      ),
      content_type: "Article",
      description,
      object_id: updatedAssignment?.content_id,
    },
  });

  const eventProperties = isSavedAssignment
    ? getEventProperties(actionNames.save, "Save draft assignment")
    : getEventProperties(actionNames.save, "Save as draft assignment");

  // Toasts
  const successMessage = `You have successfully saved ${isSavedAssignment ? "this" : "a"} draft.`;
  const saveSuccessToastProps = getSuccessToastProps(
    successMessage,
    currentContentLevelId,
    updatedAssignment?.content_id,
  );

  const saveErrorMessage = "Draft save failed, please try again";
  const saveErrorToastProps = getErrorToastProps(saveErrorMessage);

  const invalidDraftErrorMessage = `Please add a title to save this ${isSavedAssignment ? "draft assignment" : "assignment as a draft"}`;
  const invalidDraftToastProps = getErrorToastProps(invalidDraftErrorMessage);

  const handleSaveDraft = async () => {
    if (isDisabled) return;
    const isValidAssignment = getIsAssignmentValid(updatedAssignment, true);
    if (!isValidAssignment) {
      return globalToasterRef.current.displayToast(invalidDraftToastProps);
    }
    const assignment = await handleSave({
      eventProperties,
      saveSuccessToastProps,
      saveErrorToastProps,
      isDraft: true,
    });
    focusElement(focusElementRefKeys.assignmentEditorHeading);
    return assignment;
  };

  return { handleSaveDraft, isPublishedAssignment, isSavedAssignment };
};

export default useSaveDraftAssignment;
